import { supabase } from '../../supabase';
import { capturePayment } from '../mercado_pago';
import { toast } from 'react-toastify';
import { Tasks } from '../../utils/types';
import { sendPushNotification } from './mobile_notifications';

class TaskStatusService {
  private static instance: TaskStatusService;

  private constructor() {}

  public static getInstance(): TaskStatusService {
    if (!TaskStatusService.instance) {
      TaskStatusService.instance = new TaskStatusService();
    }
    return TaskStatusService.instance;
  }

  async changeTaskStatus(
    taskId: string,
    newStatus: string
  ): Promise<Tasks | null> {
    try {
      const { data: task, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('id', taskId)
        .single();

      if (error) throw error;
      if (!task) throw new Error('Task not found');

      // Handle specific status changes
      switch (newStatus) {
        case 'assigned':
          await this.handleAssignedStatus(task);
          break;
        case 'complete':
          await this.handleCompletedStatus(task);
          break;
        // Add more cases as needed
      }

      const { data: updatedTask, error: updateError } = await supabase
        .from('tasks')
        .update({ status: newStatus })
        .eq('id', taskId)
        .select('*')
        .single();

      if (updateError) throw updateError;

      // Send push notification for the status change
      await this.sendStatusChangeNotification(updatedTask);

      return updatedTask;
    } catch (error) {
      console.error('Error changing task status:', error);
      throw error;
    }
  }

  private async handleAssignedStatus(task: Tasks): Promise<void> {
    if (task.payment_method === 'card') {
      const { data: existingPayment } = await supabase
        .from('asker_payments')
        .select('id')
        .eq('task_id', task.id)
        .single();

      if (!existingPayment) {
        try {
          await capturePayment(
            task.id.toString(),
            task.asker_id?.toString() || ''
          );
        } catch (error) {
          console.error('Error capturing payment:', error);
          toast.error(
            'Error al procesar el pago. El estado de la tarea no se actualizó.'
          );
          throw error;
        }
      }
    }
  }

  private async handleCompletedStatus(task: Tasks): Promise<void> {
    // Implement any specific logic for completed tasks
    console.log('Task completed:', task.id);
  }

  private async sendStatusChangeNotification(task: Tasks): Promise<void> {
    const notificationData = this.getNotificationData(
      task.status,
      task.category
    );
    if (!notificationData) return;

    const { title, body } = notificationData;

    try {
      // Fetch the asker's auth_id
      const { data: asker, error: askerError } = await supabase
        .from('askers')
        .select('auth_id')
        .eq('id', task.asker_id)
        .single();

      if (askerError || !asker) {
        console.error(`Error fetching asker for task ${task.id}:`, askerError);
        return;
      }

      const userToken = asker.auth_id;
      if (!userToken) {
        console.log(`No auth_id found for asker ${task.asker_id}`);
        return;
      }

      await sendPushNotification(userToken, title, body, {
        type: 'general_notification',
        taskId: task.id,
        newStatus: task.status,
        navigation: {
          screen: 'TaskDetails',
          params: {
            taskId: task.id
          }
        }
      });

      console.log(
        `Push notification sent for task ${task.id} status change to ${task.status}`
      );
    } catch (error) {
      console.error(
        `Error sending push notification for task ${task.id}:`,
        error
      );
    }
  }

  private getNotificationData(
    status: string,
    category: string
  ): { title: string; body: string } | null {
    console.log('category', category);
    switch (status) {
      case 'assigned':
        return {
          title: 'Tu Tasker está listo para ayudarte!',
          body: 'Hemos asignado al mejor Tasker para cumplir con tu Task. 🎉',
        };
      case 'accepted':
        return {
          title: 'Task Aceptada',
          body: `Tu Task ha sido aceptada!.`,
        };
      case 'rejected':
        return {
          title: 'Task Rechazada',
          body: `Tu Task ha sido rechazada.`,
        };
      case 'completed':
      case 'complete':
        return {
          title: 'Task Completada',
          body: `Tu Task ha sido marcada como completada!`,
        };
      case 'in_progress':
      case 'progress':
        return {
          title: 'Task en Progreso',
          body: `Tu Task ha comenzado y está en progreso.`,
        };
      case 'in_application':
        return {
          title: 'Estamos buscando a tu Tasker!',
          body: `Tu Task está recibiendo postulaciones de taskers.`,
        };
      default:
        console.log(`Unhandled task status for notifications: ${status}`);
        return null;
    }
  }
}

export default TaskStatusService.getInstance();
